import { useDispatch, useSelector } from "react-redux";
import {
  setStatus,
  setTestResult,
  setPingLoading,
  setReconnectLoading,
  setVersionLoading,
  setRefreshing,
  setError,
} from "../../store/slices/qztray/qzTrayConnectionSlice";

import { RootState } from "../../store/store";
import useApiErrorHandler from "../useApiErrorHandler";
import useAlerts from "../useAlerts";
import QzTrayConnectionService from "../../services/qztray/QzTrayConnectionService";

/**
 * 🌐 Hook: useQzTrayConnection
 *
 * Gestiona toda la lógica del módulo QZ Tray Connection:
 * - Ping de prueba
 * - Estado general y en vivo
 * - Reintentos y validación de versión
 * - Auto-refresh para el monitor visual
 */
export const useQzTrayConnection = () => {
  const dispatch = useDispatch();
  const {
    status,
    testResult,
    isPingLoading,
    isReconnectLoading,
    isVersionLoading,
    isRefreshing,
    error,
  } = useSelector((state: RootState) => state.qzTrayConnection);

  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();

  // ========================================================
  // 🧪 Prueba puntual de conexión (Ping)
  // ========================================================
  const pingConnection = async () => {
    try {
      dispatch(setPingLoading(true));
      const response = await QzTrayConnectionService.testConnection();
      const { statusCode, data } = response;

      if (statusCode === 200 || statusCode === 503) {
        dispatch(setTestResult(data));
        showAlert(
          statusCode === 200 ? "success" : "warning",
          "Prueba de Conexión",
          data?.message || "Resultado del test de conexión"
        );
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("Error al probar conexión."));
      showAlert("error", "Error", "No se pudo probar la conexión con QZ Tray.");
    } finally {
      dispatch(setPingLoading(false));
    }
  };

  // ========================================================
  // 📊 Estado general del cliente QZ Tray
  // ========================================================
  const fetchStatus = async () => {
    try {
      dispatch(setPingLoading(true));
      const response = await QzTrayConnectionService.getStatus();
      const { statusCode, data } = response;

      if (statusCode === 200 || statusCode === 503) {
        dispatch(setStatus(data));
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("No se pudo obtener el estado general."));
    } finally {
      dispatch(setPingLoading(false));
    }
  };

  // ========================================================
  // 📈 Estado en vivo (para monitores o dashboards)
  // ========================================================
  const fetchLiveStatus = async () => {
    try {
      dispatch(setRefreshing(true));
      const response = await QzTrayConnectionService.getLiveStatus();
      const { statusCode, data } = response;

      if (statusCode === 200 || statusCode === 503) {
        dispatch(setStatus(data));
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("No se pudo actualizar el estado en vivo."));
    } finally {
      dispatch(setRefreshing(false));
    }
  };

  // ========================================================
  // ♻️ Reintentar conexión manualmente
  // ========================================================
  const reconnectClient = async () => {
    try {
      dispatch(setReconnectLoading(true));
      const response = await QzTrayConnectionService.reconnect();
      const { statusCode, data, message } = response;

      if (statusCode === 200 && data === true) {
        showAlert(
          "success",
          "Reconexión exitosa",
          message || "QZ Tray se ha reconectado correctamente."
        );
        await fetchLiveStatus();
      } else {
        showAlert(
          "warning",
          "Reconexión fallida",
          message || "No fue posible reconectar con QZ Tray."
        );
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("Error al intentar reconectar con QZ Tray."));
      showAlert("error", "Error", "No se pudo realizar la reconexión con QZ Tray.");
    } finally {
      dispatch(setReconnectLoading(false));
    }
  };

  // ========================================================
  // 🧩 Verificar compatibilidad de versión
  // ========================================================
  const checkVersion = async () => {
    try {
      dispatch(setVersionLoading(true));
      const response = await QzTrayConnectionService.checkVersion();
      const { statusCode, data, message } = response;

      if (statusCode === 200) {
        showAlert(
          data ? "success" : "warning",
          "Compatibilidad de Versión",
          message
        );
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("Error al verificar versión de QZ Tray."));
      showAlert("error", "Error", "No se pudo verificar la versión de QZ Tray.");
    } finally {
      dispatch(setVersionLoading(false));
    }
  };

  // ========================================================
  // 🔁 Auto-refresh (para monitor visual)
  // ========================================================
  const startAutoRefresh = (intervalMs = 10000) => {
    const interval = setInterval(fetchLiveStatus, intervalMs);
    return () => clearInterval(interval);
  };

  // ========================================================
  // 🧩 Retorno del hook
  // ========================================================
  return {
    status,
    testResult,
    isPingLoading,
    isReconnectLoading,
    isVersionLoading,
    isRefreshing,
    error,
    pingConnection,
    fetchStatus,
    fetchLiveStatus,
    reconnectClient,
    checkVersion,
    startAutoRefresh,
  };
};
